<script setup>
import { computed, watch, onMounted } from 'vue';
import ReportMetricCard from './ReportMetricCard.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { STATUS } from 'dashboard/store/constants';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const metrics = useMapGetter('getBotMetrics');
const fetchStatus = useMapGetter('getBotMetricsFetchingStatus');
const isLoading = computed(() => fetchStatus.value === STATUS.FETCHING);
const hasError = computed(() => fetchStatus.value === STATUS.FAILED);
const isUnavailable = computed(() => isLoading.value || hasError.value);

const conversationCount = computed(() =>
  isUnavailable.value ? '--' : metrics.value.conversation_count.toLocaleString()
);
const messageCount = computed(() =>
  isUnavailable.value ? '--' : metrics.value.message_count.toLocaleString()
);
const resolutionRate = computed(() =>
  isUnavailable.value ? '' : metrics.value.resolution_rate.toString()
);
const handoffRate = computed(() =>
  isUnavailable.value ? '' : metrics.value.handoff_rate.toString()
);

const formatToPercent = value => {
  return value ? `${value}%` : '--';
};

const fetchMetrics = async () => {
  if (!props.filters.to || !props.filters.from) {
    return false;
  }
  try {
    await store.dispatch('fetchBotMetrics', props.filters);
    return true;
  } catch {
    return false;
  }
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);
</script>

<template>
  <div
    class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 px-6 py-5"
  >
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')"
      :value="conversationCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
      :value="messageCount"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP')"
      :value="formatToPercent(resolutionRate)"
      class="flex-1"
    />
    <ReportMetricCard
      :label="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL')"
      :info-text="$t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP')"
      :value="formatToPercent(handoffRate)"
      class="flex-1"
    />
    <Banner
      v-if="hasError"
      color="ruby"
      :action-label="$t('BOT_REPORTS.METRIC.RETRY')"
      class="basis-full w-full mt-4"
      data-testid="bot-metrics-error"
      @action="fetchMetrics"
    >
      {{ $t('BOT_REPORTS.METRIC.LOAD_ERROR') }}
    </Banner>
  </div>
</template>
